import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { REWARD_ICONS, useSettings, type RewardIcon } from '../settings/SettingsContext';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import {
  fetchRedemptions,
  fetchRewardItems,
  fetchRewardSummary,
  redeemReward,
  type Redemption,
  type RewardItem,
  type RewardSummary,
} from '../rewards/rewards';

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export default function RewardsPage() {
  const t = useT();
  const { learnerId, learners } = useLearner();
  const { settings } = useSettings();
  const [summary, setSummary] = useState<RewardSummary | null>(null);
  const [items, setItems] = useState<RewardItem[]>([]);
  const [collected, setCollected] = useState<Redemption[]>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [justCollected, setJustCollected] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await fetchRewardSummary(learnerId);
      setSummary(s);
      if (s?.spending) {
        const [list, history] = await Promise.all([
          fetchRewardItems(),
          fetchRedemptions(learnerId),
        ]);
        setItems(list);
        setCollected(history);
      } else {
        setItems([]);
        setCollected([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not load that just now.'));
    } finally {
      setLoading(false);
    }
  }, [learnerId]);

  useEffect(() => { void load(); }, [load]);

  async function collect(item: RewardItem) {
    setBusy(item.id);
    setError(null);
    setJustCollected(null);
    try {
      await redeemReward(item.id, learnerId);
      setJustCollected(item.name);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be collected.'));
    } finally {
      setBusy(null);
    }
  }

  const name = summary?.name ?? t('stars');
  const glyph = REWARD_ICONS[(summary?.icon ?? 'star') as RewardIcon] ?? REWARD_ICONS.star;
  const balance = summary?.total ?? 0;
  const whose = learners.length > 1 && learnerId
    ? learners.find((l) => l.id === learnerId)?.name ?? null
    : null;

  const shown = filter.trim()
    ? items.filter((i) => i.name.toLowerCase().includes(filter.trim().toLowerCase()))
    : items;

  return (
    <section aria-labelledby="rewards-h">
      <div className="week-bar">
        <h2 id="rewards-h" className="week-title">
          {whose ? t("{learner}'s {reward}", { learner: whose, reward: name }) : t('Collect with {reward}', { reward: name })}
        </h2>
        <div className="toolbar">
          <Link className="btn secondary" to="/">{t('Done')}</Link>
        </div>
      </div>

      <div className="balance-bar" role="status">
        <span className="balance-glyph" aria-hidden="true">{glyph}</span>
        <strong className="balance-figure">{balance}</strong>
        <span className="balance-name">{t('{reward} left to spend', { reward: name })}</span>
        {summary && (
          <span className="balance-detail">
            {t('{earned} collected all together, {spent} spent', { earned: summary.earned, spent: summary.spent })}
          </span>
        )}
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {justCollected && (
        <div className="banner" role="status">
          <span aria-hidden="true">{glyph}</span> {t('{item} — collected.', { item: justCollected })}
        </div>
      )}

      {loading ? (
        <p className="muted">{t('Loading…')}</p>
      ) : !summary?.enabled ? (
        <div className="card">
          <h2>{t('Rewards are switched off')}</h2>
          <p className="field-hint">
            {t('Turn them on under Settings, Rewards, and what this learner collects will appear here.')}
          </p>
        </div>
      ) : !summary.spending ? (
        <div className="card">
          <h2>{t('There is nothing to spend these on')}</h2>
          <p className="field-hint">
            {t('{reward} are being collected, but choosing things to spend them on is switched off.', { reward: name })}
            {' '}{t('Turn on "Let them spend what they collect" under Settings, Features.')}
          </p>
        </div>
      ) : (
        <>
          <div className="card">
            <h2>{t('Things to choose')}</h2>
            {items.length === 0 ? (
              <p className="muted">
                {t('Nothing on the list yet. Add things under Settings, Rewards.')}
              </p>
            ) : (
              <>
                {items.length > 8 && (
                  <div className="field">
                    <label htmlFor="reward-filter">{t('Find something')}</label>
                    <input
                      id="reward-filter"
                      value={filter}
                      placeholder={t('Start typing a name')}
                      onChange={(e) => setFilter(e.target.value)}
                    />
                  </div>
                )}
                {shown.length === 0 ? (
                  <p className="muted">{t('Nothing on the list matches that.')}</p>
                ) : (
                  <ul className="list">
                    {shown.map((item) => {
                      const tooDear = item.cost > balance;
                      return (
                        <li className="list-item" key={item.id}>
                          <div className="list-item-text">
                            <div className="name">{item.name}</div>
                            <div className="list-item-meta">
                              {item.cost === 0
                                ? t('Costs nothing')
                                : t('{cost} {reward}', { cost: item.cost, reward: name })}
                              {tooDear && ` — ${t('{n} more needed', { n: item.cost - balance })}`}
                            </div>
                          </div>
                          <div className="toolbar">
                            <button
                              type="button"
                              className="btn"
                              disabled={busy !== null || tooDear}
                              onClick={() => { void collect(item); }}
                            >
                              {busy === item.id ? t('Collecting…') : t('Collect')}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>

          <div className="card">
            <h2>{t('Already collected')}</h2>
            {collected.length === 0 ? (
              <p className="muted">{t('Nothing collected yet.')}</p>
            ) : (
              <ul className="list">
                {collected.map((r) => (
                  <li className="list-item" key={r.id}>
                    <div className="list-item-text">
                      <div className="name">{r.itemName}</div>
                      <div className="list-item-meta">
                        {r.cost === 0 ? t('Cost nothing') : t('{cost} {reward}', { cost: r.cost, reward: name })}
                        {when(r.collectedAt) && ` · ${when(r.collectedAt)}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="field-hint">
              {t('Collecting never takes away what was earned. The work behind these {reward} is still counted everywhere else.', { reward: name })}
            </p>
          </div>
        </>
      )}

      {settings.features.preferenceAssessment && (
        <div className="toolbar">
          <Link className="btn secondary" to="/preferences" aria-label={t('Put the rewards in order')}><span className="label-long">{t('Put the rewards in order')}</span><span className="label-short" aria-hidden="true">{t('Rank rewards')}</span></Link>
        </div>
      )}
    </section>
  );
}
